import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { useContextSelector } from "use-context-selector";
import useScrollToItem from "../../component/Scroll";
import AuthContext from "../../context/AuthContext";
import BetSlipContext from "../../context/BetSlipContext";

const useSportsBookLogic = ({
  data,
  user,
  detail,
  profileData,
  setBetSlipObject,
  favouriteList,
}) => {
  // Context
    const location = useLocation();
  let loc = location?.pathname?.split("/");
  const parlyBet = useContextSelector(AuthContext, (ctx) => ctx.parlyBet);
  const oneclickActive = useContextSelector(
    AuthContext,
    (ctx) => ctx.oneclickActive
  );
  const parlay = useContextSelector(AuthContext, (ctx) => ctx.parlay);
  const oneClickBid = useContextSelector(AuthContext, (ctx) => ctx.oneClickBid);

  const betSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.betSlipObject
  );
  const showBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.showBetDialog
  );
  const setShowBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setShowBetDialog
  );
  const setLimit = useContextSelector(BetSlipContext, (ctx) => ctx.setLimit);
  const setOneClickBetLoader = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setOneClickBetLoader
  );
  const handelPlaceSportBook = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.handelPlaceSportBook
  );

  // States
  const [header, setHeader] = useState("all");
  const [selected, setSelected] = useState("");
  const [active, setActive] = useState(
    Array.from({ length: 101 }, (_, i) => i)
  );
  const [localLimit, setLocalLimit] = useState({});
  const [updatedData, setUpdatedData] = useState([]);

  const navigate = useNavigate();
  const scrollToItem = useScrollToItem("#header");

  // ✅ Handle sportsbook betting logic
  const handelChange = (odds, item, res, e) => {
    if (!isEmpty(user)) {
      if (odds > 0) {
        setShowBetDialog(true);

     if (loc[1] != "match-detail-sky") {
        scrollToItem(e.currentTarget);
      }
        let object = {
          odds,
          eventId: detail?.eventId,
          marketId: detail?.marketId,
          teamName: res?.selectionName,
          item: { ...item },
          playType: "sportBook",
          object: { ...res },
        };

        setLimit(localLimit);
        setBetSlipObject((prev) => ({ ...prev, ...object }));

        // if (oneclickActive) {
        //   setOneClickBetLoader("sportBook");
        //   handelPlaceSportBook(localLimit, {
        //     ...object,
        //     bidPrice: oneClickBid,
        //   });
        // }
      }
    } else {
      navigate("/login");
    }
  };

  // ✅ Load local limit settings
  useEffect(() => {
    let obj = profileData?.matchSetting?.find(
      (res) =>
        res?.type === "sportBook" &&
        (profileData?.arrayCheck === "details"
          ? true
          : res?.sportType === detail?.gameType)
    );
    if (!isEmpty(obj)) {
      setLocalLimit(obj);
    } else {
      setLocalLimit({
        maxAmount: 1000,
        maxProfit: 10000,
        minAmount: 1,
        oddsLimit: 10,
      });
    }
  }, [profileData, detail]);

  // ✅ Highlight parlay selections
  const sportBookCheck = (res) => {
    if (isEmpty(user)) return "";
    const find = parlyBet?.find((p) => p?.newEventId === detail?.eventId);
    if (!isEmpty(find)) {
      return find?.sportsBookSelectionObject?.id === res?.id
        ? "active-sportBook-parly"
        : "";
    }
    return "";
  };

  // ✅ Reorder data based on favourite list
  const reorderedData = useMemo(() => {
    if (!favouriteList?.length) return data || [];
    const favSet = new Set(favouriteList);
    return [
      ...data.filter((item) => favSet.has(item.id)),
      ...data.filter((item) => !favSet.has(item.id)),
    ];
  }, [data, favouriteList]);

  // ✅ Filtered data based on header
  const filteredData = useMemo(() => {
    if (!reorderedData?.length) return [];
    const lowerCased = (str) => str?.toLowerCase() || "";

    switch (header) {
      case "Innings":
        return reorderedData.filter((res) =>
          lowerCased(res.marketName).includes("innings")
        );
      case "Over":
        return reorderedData.filter((res) => {
          const name = lowerCased(res.marketName);
          return name.includes("over") && !name.includes("winner");
        });
      case "Match":
        return reorderedData.filter((res) => {
          const name = lowerCased(res.marketName);
          return name.includes("winner") || name.includes("innings");
        });
      case "Batsman":
        return reorderedData.filter((res) => {
          const name = lowerCased(res.marketName);
          return (
            !name.includes("over") &&
            name.includes("innings") &&
            name.includes("total")
          );
        });
      default:
        return reorderedData;
    }
  }, [reorderedData, header]);

  useEffect(() => {
    setUpdatedData(filteredData);
  }, [filteredData]);

  // ✅ Sync bet slip updates
  useEffect(() => {
    if (!(betSlipObject?.playType === "sportBook" && showBetDialog)) return;

    // During live updates `data` can be temporarily empty.
    // Don't close the bet slip in that case; just wait for the next payload.
    if (!data?.length) return;

    const find = data.find((res) => res?.id === betSlipObject?.item?.id);

    // Keep bet slip open while odds are changing.
    // If the selection becomes truly invalid, the user can close manually.
    if (
      isEmpty(find) ||
      !find?.sportsBookSelection?.length ||
      ![1].includes(Number(find?.marketStatus)) // only active markets
    )
      return;

    const updatedObject = find.sportsBookSelection.find(
      (res) => res.id === betSlipObject?.object?.id
    );

    if (isEmpty(updatedObject)) return;

    if (updatedObject?.odds !== betSlipObject?.odds) {
      setBetSlipObject((prev) => ({
        ...prev,
        object: { ...prev.object, odds: updatedObject?.odds },
        odds: updatedObject?.odds,
      }));
    }
  }, [betSlipObject, showBetDialog, data, setBetSlipObject]);

  return {
    header,
    setHeader,
    selected,
    setSelected,
    active,
    setActive,
    localLimit,
    handelChange,
    sportBookCheck,
    updatedData,
    reorderedData,
    filteredData,
    parlyBet,
    parlay,
    betSlipObject,
    showBetDialog,
  };
};

export default useSportsBookLogic;
